// Shared time formatting for the Stealth Clock module — ORG-TIMEZONE AUTHORITATIVE.
//
// Every instant rendered by this module is shown in the ORGANIZATION's timezone, never
// the viewer's. That matters because the server computes day boundaries and totals in
// the org zone: if the client rendered in local time, an employee travelling (or an
// admin in another country) would see times that contradict the day they belong to.
//
// Organization.settings.timezone is NOT always an IANA name. Real values in this
// product include "Asia/Kolkata", "UTC", "UTC-5", "UTC+5:30" and "Europe/Dublin".
// Intl.DateTimeFormat throws a RangeError on the fixed-offset forms, so resolveZone()
// below mirrors server/utils/orgFilterRange.js `resolveZone` EXACTLY — same regex, same
// fallbacks — so the client and server can never disagree about what time it is.

/**
 * Mirror of the backend resolveZone(). Returns:
 *   { type:'iana', zone }      → format via Intl
 *   { type:'offset', minutes } → shift the instant manually
 *   { type:'none' }            → UTC (the backend's fallback too)
 */
function resolveZone(tz) {
  const s = String(tz || '').trim();
  if (!s) return { type: 'none' };

  // IANA zones are whatever Intl accepts.
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: s }).format(new Date());
    return { type: 'iana', zone: s };
  } catch (_) {
    /* not an IANA zone — try the fixed-offset forms below */
  }

  if (/^(UTC|GMT)$/i.test(s)) return { type: 'offset', minutes: 0 };
  const m = s.match(/^(?:UTC|GMT)?\s*([+-])(\d{1,2})(?::?(\d{2}))?$/i);
  if (m) {
    const sign = m[1] === '-' ? -1 : 1;
    return {
      type: 'offset',
      minutes: sign * ((parseInt(m[2], 10) || 0) * 60 + (parseInt(m[3] || '0', 10) || 0)),
    };
  }
  return { type: 'none' };
}

/** Hour/minute of `d` as seen in zone `z`. */
function clockParts(d, z) {
  if (z.type === 'iana') {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: z.zone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: false,
    }).formatToParts(d);
    const get = (type) => Number(parts.find((p) => p.type === type)?.value ?? 0);
    // hour12:false can yield "24" at midnight in some engines — normalise.
    return { h: get('hour') % 24, m: get('minute') };
  }
  // Fixed offset (or unknown → UTC): shift the instant, then read UTC fields.
  const shifted = new Date(d.getTime() + (z.type === 'offset' ? z.minutes : 0) * 60000);
  return { h: shifted.getUTCHours(), m: shifted.getUTCMinutes() };
}

/** "9:12 am" in the ORG's timezone. */
export function fmtTime(value, tz) {
  if (!value) return '';
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return '';

  const { h, m } = clockParts(d, resolveZone(tz));
  const ap = h >= 12 ? 'pm' : 'am';
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${String(m).padStart(2, '0')} ${ap}`;
}

/**
 * Short, human label for a timezone, for use next to times ("Today · IST").
 * Falls back to the raw configured string, which is already meaningful ("UTC+5:30").
 */
export function tzLabel(tz) {
  const s = String(tz || '').trim();
  if (!s) return 'UTC';
  const z = resolveZone(s);
  if (z.type === 'iana') {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: s,
        timeZoneName: 'short',
      }).formatToParts(new Date());
      const name = parts.find((p) => p.type === 'timeZoneName')?.value;
      if (name) return name;
    } catch (_) {
      /* fall through */
    }
    return s;
  }
  if (z.type === 'offset') {
    if (z.minutes === 0) return 'UTC';
    const sign = z.minutes < 0 ? '-' : '+';
    const abs = Math.abs(z.minutes);
    const hh = Math.floor(abs / 60);
    const mm = abs % 60;
    return `UTC${sign}${hh}${mm ? `:${String(mm).padStart(2, '0')}` : ''}`;
  }
  return 'UTC';
}

/**
 * Seconds → running-timer form: "6:43:12" / "43:12". Never negative.
 *
 * Used for the live on-duty counter, where a ticking seconds field is the whole
 * point. Elapsed is always recomputed from the shift's start instant rather than
 * accumulated per tick, so the display stays exact after a sleep/wake or a
 * throttled background tab.
 */
export function fmtTimer(totalSec) {
  const s = Math.max(0, Math.floor(Number(totalSec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(sec).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
}

/** Seconds → "8h 31m" / "47m" / "0m". Never negative. */
export function fmtDuration(totalSec) {
  const s = Math.max(0, Math.floor(Number(totalSec) || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  return `${m}m`;
}

/**
 * "Mon, 17 Aug" for a YYYY-MM-DD day label.
 *
 * The label already IS the org-local day (the server bucketed it), so it is parsed
 * from its parts rather than through Date(string), which would treat it as UTC and
 * could slip a day backwards for viewers behind UTC.
 */
export function fmtDayLabel(dateStr) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(dateStr || ''))) return '';
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
  });
}

/**
 * "Today" in the ORG's timezone as YYYY-MM-DD — the correct default for the admin
 * date picker. Using the viewer's local today would ask the server for the wrong day
 * whenever the viewer and the organization are on opposite sides of midnight.
 */
export function todayInOrgTz(tz) {
  const z = resolveZone(tz);
  const now = new Date();
  if (z.type === 'iana') {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: z.zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now);
    const get = (t) => parts.find((p) => p.type === t)?.value;
    return `${get('year')}-${get('month')}-${get('day')}`;
  }
  const shifted = new Date(now.getTime() + (z.type === 'offset' ? z.minutes : 0) * 60000);
  return `${shifted.getUTCFullYear()}-${String(shifted.getUTCMonth() + 1).padStart(2, '0')}-${String(
    shifted.getUTCDate()
  ).padStart(2, '0')}`;
}

/** Viewer-local today (kept for the initial render, before the org tz is known). */
export function todayLocalISO() {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
}
